/**
 * SDT Shared Mappers
 * Unified mapping functions for API data normalization.
 * Import these in every context provider to ensure consistent data shapes.
 */

/**
 * Map a raw customer/student API record to a normalized shape.
 */
export const mapCustomer = (c) => {
    if (!c) return null;
    let docs = [];
    try {
        docs = c.documents ? JSON.parse(c.documents) : [];
    } catch (e) {
        docs = c.documents ? String(c.documents).split(',').filter(Boolean) : [];
    }
    return {
        ...c,
        id: c.id,
        name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown Student',
        instructorId: (c.instructor_id && typeof c.instructor_id === 'object') ? c.instructor_id.id : c.instructor_id,
        branchId: (c.branch_id && typeof c.branch_id === 'object') ? c.branch_id.id : c.branch_id,
        branch: (c.branch && typeof c.branch === 'object') ? (c.branch.branch_name || c.branch.name || '') : (c.branch || ''),
        userId: c.user_id,
        documents: Array.isArray(docs) ? docs : [],
        phone: c.phone_number || c.phone || '',
        email: c.email_address || c.email || '',
        idNumber: c.national_id__no || c.idNumber || '',
        nextOfKin: {
            name: c.next_of_kin_name || '',
            relationship: c.relationship_with_next_of_kin || '',
            phone: c.next_of_kin_phone_number || ''
        },
        lessonsRemaining: c.lessons_remaining,
        enrollmentDate: c.enrollment_date,
        dateOfBirth: c.date_of_birth,
        stage: c.stage || 'New',
        balance: parseFloat(c.balance || 0),
        notes: c.notes || ''
    };
};

/**
 * Map a raw lead API record to a normalized shape.
 */
export const mapLead = (l) => {
    if (!l) return null;
    return {
        ...l,
        id: l.id || l.lead_id || Math.random(),
        name: (l.first_name && l.last_name) ? `${l.first_name} ${l.last_name}` : (l.name || 'Unknown User'),
        phone: l.phone_number || l.phone || '',
        email: l.email || '',
        source: l.lead_source || l.source || 'Other',
        branch: (l.branch_name && typeof l.branch_name === 'string') ? l.branch_name : (l.branch || ''),
        branchId: l.branch_id || '',
        interest: (l.interest && typeof l.interest === 'object') ? (l.interest.name || l.interest.id) : l.interest,
        status: l.status || 'New',
        notes: l.notes || '',
        date: l.date || l.created_at || new Date().toISOString()
    };
};

/**
 * Map a raw payment API record to a normalized shape.
 */
export const mapPayment = (p) => {
    if (!p) return null;
    return {
        ...p,
        id: p.id,
        customerId: p.customer_id || p.customerId,
        amount: parseFloat(p.amount || 0),
        date: p.payment_date || p.date,
        method: p.method || 'Cash',
        description: p.description || '',
        branchId: p.branch_id || ''
    };
};

/**
 * Map a raw expense API record to a normalized shape.
 * Note: the DB column is expense_date, NOT payment_date (§3.1 fix).
 */
export const mapExpense = (e) => {
    if (!e) return null;
    return {
        ...e,
        id: e.id,
        amount: parseFloat(e.amount || 0),
        date: e.expense_date || e.date,
        category: e.category || 'Other',
        method: e.method || 'Cash',
        description: e.description || '',
        branchId: e.branch_id || ''
    };
};

/**
 * Map a raw package API record to a normalized shape.
 */
export const mapPackage = (p) => {
    if (!p) return null;
    return {
        ...p,
        id: p.id,
        packageId: p.package_id,
        name: p.package_name || p.name || 'Unnamed Package',
        price: parseFloat(p.customer_price || p.price || 0),
        costPrice: parseFloat(p.package_cost_price || p.cost_price || 0),
        lessonsCount: parseInt(p.lesson_count || p.lessons_count || 0),
        description: p.package_details || p.description || ''
    };
};

/**
 * Map a raw staff/instructor API record to a normalized shape.
 */
export const mapStaff = (s) => {
    if (!s) return null;
    return {
        ...s,
        id: s.id,
        name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown Staff',
        phone: s.phone_number || s.phone || '',
        email: s.email_address || s.email || '',
        role: s.assigned_role || s.role || 'Staff',
        status: s.status || 'Active',
        branchId: s.branch_id,
        userId: s.user_id,
        userLogin: s.user_login
    };
};

/**
 * Map a raw promotion API record to a normalized shape.
 */
export const mapPromotion = (p) => {
    if (!p) return null;
    return {
        ...p,
        id: p.id,
        name: p.promotion_name || p.name,
        discountValue: parseFloat(p.discount_value || 0),
        discountType: p.discount_type,
        packageId: p.package_id,
        startDate: p.promotion_start_date || p.start_date,
        endDate: p.promotion_end_date || p.end_date
    };
};
